import React, { useState, useEffect } from 'react';
import { getJobDetails } from '../services/api';
import './JobDetailsModal.css';

function JobDetailsModal({ jobId, onClose }) {
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    description: false,
    structured: true,
    constraints: true,
    insights: true,
    query: true,
  });

  const fetchJobData = async () => {
    try {
      setLoading(true);
      const data = await getJobDetails(jobId);
      setJobData(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobData();
  }, [jobId]);

  // Poll for updates when processing
  useEffect(() => {
    if (jobData?.status === 'processing') {
      const interval = setInterval(() => {
        fetchJobData();
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(interval);
    }
  }, [jobData?.status]);


  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusColor = (status) => {
    const statusColors = {
      new: '#9ca3af',
      processing: '#f59e0b',
      matching: '#8b5cf6',
      shortlisting: '#fbbf24',
      ready: '#10b981',
      matched: '#8b5cf6',
      shortlisted: '#a78bfa',
      explaining: '#8b5cf6',
      outreach_sent: '#10b981',
      intent_received: '#06b6d4',
      failed: '#ef4444',
      closed: '#6b7280',
    };
    return statusColors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      new: 'New',
      processing: 'Processing',
      matching: 'Matching',
      shortlisting: 'Shortlisting',
      ready: 'Ready for Matching',
      matched: 'Ready for Outreach',
      shortlisted: 'Shortlisted',
      explaining: 'Explaining',
      outreach_sent: 'Outreach Sent',
      intent_received: 'Intent Received',
      failed: 'Failed',
      closed: 'Closed',
    };
    return statusLabels[status] || status;
  };

  const normalizePracticeArea = (practiceArea) => {
    if (!practiceArea) return practiceArea;
    
    // Normalize common variations to consistent names
    const normalizationMap = {
      'Ip': 'IP',
      'Patent': 'IP',
      'Intellectual Property': 'IP',
      'Real Estate': 'Real Estate',
      'Real estate': 'Real Estate',
      'Employment': 'Employment',
      'Corporate': 'Corporate',
      'Litigation': 'Litigation',
      'Tax': 'Tax',
      'Healthcare': 'Healthcare',
      'Criminal': 'Criminal',
      'Family': 'Family',
      'Immigration': 'Immigration'
    };
    
    return normalizationMap[practiceArea] || practiceArea;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">Loading job details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">Error: {error}</div>
          <button className="btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const hasArtifacts = jobData?.ai_artifacts;
  const artifacts = jobData?.ai_artifacts || {};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-top">
            <div className="modal-title-row">
              <span className="modal-title-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="#3b82f6"/>
                </svg>
              </span>
              <h2 className="modal-title">{jobData?.title}</h2>
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor(jobData?.status) }}
              >
                {getStatusLabel(jobData?.status)}
              </span>
            </div>
            <button className="btn-close-icon" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div className="modal-metadata">
            <div className="modal-metadata-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7V3H2V21H22V7H12ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V17H14V15H12V13H14V11H12V9H20V19ZM18 11H16V13H18V11ZM18 15H16V17H18V15Z" fill="currentColor"/>
              </svg>
              <span>{jobData?.company}</span>
            </div>
            <div className="modal-metadata-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
              </svg>
              <span>{jobData?.location || 'Remote'}</span>
            </div>
            <div className="modal-metadata-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM12 13H17V18H12V13Z" fill="currentColor"/>
              </svg>
              <span>Posted: {formatDate(jobData?.created_at)}</span>
            </div>
            {jobData?.source_url && (
              <a 
                href={jobData.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="modal-view-source"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.9 12C3.9 10.29 5.29 8.9 7 8.9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15.1H7C5.29 15.1 3.9 13.71 3.9 12ZM8 13H16V11H8V13ZM17 7H13V8.9H17C18.71 8.9 20.1 10.29 20.1 12C20.1 13.71 18.71 15.1 17 15.1H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7Z" fill="currentColor"/>
                </svg>
                View Source
              </a>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="modal-content">
          {/* Show processing indicator */}
          {jobData?.status === 'processing' && (
            <div className="processing-indicator">
              <div className="processing-steps">
                <div className="processing-step active">
                  <div className="step-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="step-text">Extracting</div>
                </div>
                <div className="processing-step-connector" aria-hidden="true" />
                <div className="processing-step">
                  <div className="step-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="step-text">Ready</div>
                </div>
              </div>
              <div className="processing-spinner">
                <div className="spinner"></div>
                <p>Processing job with AI…</p>
              </div>
            </div>
          )}

          {/* Two Column Layout */}
          <div className="modal-two-column">
            {/* Left Column */}
            <div className="modal-left-column">
              {/* Card 1: Raw Job Description */}
              <div className="info-card">
                <div className="card-header" onClick={() => toggleSection('description')}>
                  <h3>
                    <span className="card-icon card-icon-blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill="currentColor"/>
                      </svg>
                    </span>
                    Raw Job Description
                  </h3>
                  <span className="expand-icon">{expandedSections.description ? '▼' : '▶'}</span>
                </div>
                {expandedSections.description && (
                  <div className="card-content">
                    <div className="description-text">
                      {artifacts.cleaned_description || jobData?.raw_description || 'No description available'}
                    </div>
                  </div>
                )}
              </div>

              {/* Card 2: AI-Extracted Structured Criteria */}
              {hasArtifacts && artifacts.structured_criteria && (
                <div className="info-card">
                  <div className="card-header" onClick={() => toggleSection('structured')}>
                    <h3>
                      <span className="card-icon card-icon-purple">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 3C9.23 3 6.19 5.95 6 9.66L4.08 12.19C3.84 12.5 4.08 13 4.5 13H6V16C6 17.11 6.89 18 8 18H9V21H15V16.31C17.37 15.19 19 12.8 19 10C19 6.13 15.87 3 12 3H13ZM13 8C13.55 8 14 8.45 14 9C14 9.55 13.55 10 13 10C12.45 10 12 9.55 12 9C12 8.45 12.45 8 13 8ZM10 9C10 8.45 9.55 8 9 8C8.45 8 8 8.45 8 9C8 9.55 8.45 10 9 10C9.55 10 10 9.55 10 9ZM15 13C14.45 13 14 12.55 14 12C14 11.45 14.45 11 15 11C15.55 11 16 11.45 16 12C16 12.55 15.55 13 15 13ZM7 12C7 11.45 7.45 11 8 11C8.55 11 9 11.45 9 12C9 12.55 8.55 13 8 13C7.45 13 7 12.55 7 12Z" fill="currentColor"/>
                        </svg>
                      </span>
                      AI-Extracted Structured Criteria
                    </h3>
                    <span className="expand-icon">{expandedSections.structured ? '▼' : '▶'}</span>
                  </div>
                  {expandedSections.structured && (
                    <div className="card-content">
                      <div className="criteria-grid">
                        {/* Skills Needed */}
                        {artifacts.structured_criteria.skills_needed && artifacts.structured_criteria.skills_needed.length > 0 && (
                          <div className="criteria-field">
                            <div className="criteria-label">SKILLS NEEDED</div>
                            <div className="criteria-chips">
                              {artifacts.structured_criteria.skills_needed.map((skill, idx) => (
                                <span key={idx} className="criteria-chip">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tools/Software */}
                        {artifacts.structured_criteria.tools_software && artifacts.structured_criteria.tools_software.length > 0 && (
                          <div className="criteria-field">
                            <div className="criteria-label">TOOLS/SOFTWARE</div>
                            <div className="criteria-chips">
                              {artifacts.structured_criteria.tools_software.map((tool, idx) => (
                                <span key={idx} className="criteria-chip">{tool}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Experience Level */}
                        {artifacts.structured_criteria.experience_level && artifacts.structured_criteria.experience_level !== 'null' && (
                          <div className="criteria-field">
                            <div className="criteria-label">EXPERIENCE LEVEL</div>
                            <div className="criteria-value">{artifacts.structured_criteria.experience_level}</div>
                          </div>
                        )}

                        {/* Practice Area */}
                        {artifacts.structured_criteria.practice_area && artifacts.structured_criteria.practice_area !== 'null' && (
                          <div className="criteria-field">
                            <div className="criteria-label">PRACTICE AREA</div>
                            <div className="criteria-value">
                              {Array.isArray(artifacts.structured_criteria.practice_area)
                                ? artifacts.structured_criteria.practice_area.map(pa => normalizePracticeArea(pa)).join(', ')
                                : normalizePracticeArea(artifacts.structured_criteria.practice_area)}
                            </div>
                          </div>
                        )}

                        {/* Seniority */}
                        {artifacts.structured_criteria.seniority && artifacts.structured_criteria.seniority !== 'null' && (
                          <div className="criteria-field">
                            <div className="criteria-label">SENIORITY</div>
                            <div className="criteria-value">{artifacts.structured_criteria.seniority}</div>
                          </div>
                        )}

                        {/* Remote Compatibility */}
                        {artifacts.structured_criteria.remote_compatibility && artifacts.structured_criteria.remote_compatibility !== 'null' && (
                          <div className="criteria-field">
                            <div className="criteria-label">REMOTE COMPATIBILITY</div>
                            <div className="criteria-value">{artifacts.structured_criteria.remote_compatibility}</div>
                          </div>
                        )}

                        {/* Education */}
                        {artifacts.structured_criteria.education && artifacts.structured_criteria.education !== 'null' && (
                          <div className="criteria-field">
                            <div className="criteria-label">EDUCATION</div>
                            <div className="criteria-value">{artifacts.structured_criteria.education}</div>
                          </div>
                        )}

                        {/* Language Requirements */}
                        {artifacts.structured_criteria.language_requirements && artifacts.structured_criteria.language_requirements.length > 0 && (
                          <div className="criteria-field">
                            <div className="criteria-label">LANGUAGE REQUIREMENTS</div>
                            <div className="criteria-value">{artifacts.structured_criteria.language_requirements.join(', ')}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Card 3: Constraints & Inferred Insights */}
              {hasArtifacts && artifacts.constraints && (
                <div className="info-card">
                  <div className="card-header" onClick={() => toggleSection('constraints')}>
                    <h3>
                      <span className="card-icon card-icon-orange">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 11.99H19C18.47 16.11 15.72 19.78 12 20.93V12H5V6.3L12 3.19V11.99Z" fill="currentColor"/>
                        </svg>
                      </span>
                      Constraints & Inferred Insights
                    </h3>
                    <span className="expand-icon">{expandedSections.constraints ? '▼' : '▶'}</span>
                  </div>
                  {expandedSections.constraints && (
                    <div className="card-content">
                      {/* Must Have */}
                      {artifacts.constraints.must_have && artifacts.constraints.must_have.length > 0 && (
                        <div className="constraint-section">
                          <h4 className="constraint-title constraint-must-have">✓ MUST HAVE</h4>
                          <ul className="constraint-list">
                            {artifacts.constraints.must_have.map((item, idx) => (
                              <li key={idx} className="constraint-item must-have">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Deal Breakers */}
                      {artifacts.constraints.deal_breakers && artifacts.constraints.deal_breakers.length > 0 && (
                        <div className="constraint-section">
                          <h4 className="constraint-title constraint-deal-breaker">✕ DEAL BREAKERS</h4>
                          <ul className="constraint-list">
                            {artifacts.constraints.deal_breakers.map((item, idx) => (
                              <li key={idx} className="constraint-item deal-breaker">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Preferences */}
                      {artifacts.constraints.preferences && artifacts.constraints.preferences.length > 0 && (
                        <div className="constraint-section">
                          <h4 className="constraint-title constraint-preference">★ PREFERENCES</h4>
                          <ul className="constraint-list">
                            {artifacts.constraints.preferences.map((item, idx) => (
                              <li key={idx} className="constraint-item preference">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Location Requirements (never in search query — filtered separately) */}
                      {artifacts.constraints.location_constraints && artifacts.constraints.location_constraints.length > 0 && (
                        <div className="constraint-section">
                          <h4 className="constraint-title constraint-location">📍 LOCATION REQUIREMENTS</h4>
                          <ul className="constraint-list">
                            {artifacts.constraints.location_constraints.map((item, idx) => (
                              <li key={idx} className="constraint-item location">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="modal-right-column">
              {/* Romeo AI Insights */}
              {hasArtifacts && artifacts.romeo_insights && (
                <div className="info-card">
                  <div className="card-header" onClick={() => toggleSection('insights')}>
                    <h3>
                      <span className="card-icon card-icon-purple">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 1C6.79 1 5 2.79 5 5V7C5 9.21 6.79 11 9 11H11V14.47L7.66 17.8C7.25 18.21 7 18.78 7 19.38V22H9V19.38L12.34 16.05C12.75 15.64 13 15.07 13 14.47V11H15C17.21 11 19 9.21 19 7V5C19 2.79 17.21 1 15 1H9ZM9 3H15C16.1 3 17 3.9 17 5V7C17 8.1 16.1 9 15 9H13V7H11V9H9C7.9 9 7 8.1 7 7V5C7 3.9 7.9 3 9 3ZM9 4C8.45 4 8 4.45 8 5C8 5.55 8.45 6 9 6C9.55 6 10 5.55 10 5C10 4.45 9.55 4 9 4ZM15 4C14.45 4 14 4.45 14 5C14 5.55 14.45 6 15 6C15.55 6 16 5.55 16 5C16 4.45 15.55 4 15 4Z" fill="currentColor"/>
                        </svg>
                      </span>
                      Romeo AI Insights
                    </h3>
                    <span className="expand-icon">{expandedSections.insights ? '▼' : '▶'}</span>
                  </div>
                  {expandedSections.insights && (
                    <div className="card-content">
                      {/* Skills Inferred */}
                      {artifacts.romeo_insights.skills_inferred && artifacts.romeo_insights.skills_inferred.length > 0 && (
                        <div className="insights-section">
                          <h4 className="insights-title">SKILLS INFERRED</h4>
                          <div className="skills-pills">
                            {artifacts.romeo_insights.skills_inferred.map((skill, idx) => (
                              <span key={idx} className="skill-pill">{skill}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risk Flags */}
                      <div className="insights-section">
                        <h4 className="insights-title">RISK FLAGS</h4>
                        <span className={`risk-badge risk-${(artifacts.romeo_insights.risk_flags || 'none').toLowerCase()}`}>
                          {artifacts.romeo_insights.risk_flags || 'None'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Search Query */}
              {hasArtifacts && (
                <div className="info-card">
                  <div className="card-header" onClick={() => toggleSection('query')}>
                    <h3>
                      <span className="card-icon card-icon-blue">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
                        </svg>
                      </span>
                      Search Query
                    </h3>
                    <span className="expand-icon">{expandedSections.query ? '▼' : '▶'}</span>
                  </div>
                  {expandedSections.query && (
                    <div className="card-content">
                      <div className="query-section">
                        <h4 className="query-title">FIND CANDIDATES WITH:</h4>
                        {artifacts.search_query_text ? (
                          <div className="query-text">
                            {artifacts.search_query_text}
                          </div>
                        ) : (
                          <div className="query-generating">
                            <div className="spinner-small"></div>
                            <span>Generating search query...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Show error message if failed */}
          {jobData?.status === 'failed' && jobData?.error_message && (
            <div className="error-card">
              <h3>Processing Error</h3>
              <p>{jobData.error_message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default JobDetailsModal;
